import { useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { DateTime } from "luxon"
import Button from "react-bootstrap/Button"
import Table from "react-bootstrap/Table"
import Form from "react-bootstrap/Form"

import "./index.scss"
import useT from "utils/hooks/useT"
import { Popup } from "components"
import { Select } from "components/Form"
import ESignatureModal from "components/Lims/ESignatureModal"
import {
  limsOos,
  limsReruns,
  limsBatchHold,
  limsQaDecisions,
  samples,
  message,
} from "state_management"
import { QA_DECISION_TYPES } from "state_management/lims/limsQaDecisions"
import {
  PHASE1_CHECK_KEYS,
  PHASE2_OUTCOME,
  CAPA_TYPE,
  CAPA_STATUS,
} from "state_management/lims/limsOos"

// Shared class for the small uppercase field labels (context items + signature captions).
const LABEL_CLASS = "OosInvestigationModal-context-label"

// The optional disposition chosen on close — reuses the existing rerun / batch-hold mechanics.
const DISPOSITION = { RERUN: "rerun", BATCH_HOLD: "batch-hold" }

// OosInvestigationModal (SPEC-018, PRD-005 Area 4) — the modal that drives a formal two-phase OOS
// investigation opened from a FAIL result in Sample View (and the QA Analysis Review worklist). It
// shows the OOS context (sample / analysis / reason), then:
//   - PHASE 1 (lab investigation): the six-point checklist (check + per-point note), an
//     assignable-cause finding, a conclusion, and the technician + supervisor signatures (each a
//     reuse of ESignatureModal). Saving Phase 1 advances the lifecycle open -> investigating.
//   - "Escalate to Phase 2" — enabled ONLY when no assignable cause was found (a lab cause means the
//     result is explained and no product investigation is warranted).
//   - PHASE 2 (product investigation): resampling, production-record review, stability assessment +
//     an outcome (lot approved / rejected / recalled) and a signature.
//   - CAPA: add / edit corrective & preventive actions embedded in the OOS (no separate menu).
//   - "Close investigation": a QA e-signature (capability "qa", 4-eyes excluding the raiser) with an
//     OPTIONAL disposition that reuses the existing rerun / batch-hold mechanics. Closing lifts the
//     release ON_HOLD.
//
// Props:
//   oosId           — the OOS investigation record id
//   onClose         — close the modal
//   onClosed        — called after a successful signed "Close investigation"
export default function OosInvestigationModal({ oosId, onClose, onClosed }) {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const oos = useSelector((state) => limsOos.selectOos(state, oosId), shallowEqual)
  const members = useSelector((state) => state.members)
  // The sample's batch/lot (F-C1 QC detail) — needed for a batch-hold disposition on close.
  const sampleQc = useSelector(
    (state) => (oos && oos.sampleId ? state.limsSampleQc[oos.sampleId] : null),
    shallowEqual
  )
  // Whether a rerun already exists for this sample (so the rerun disposition is not offered twice).
  const hasExistingRerun = useSelector((state) =>
    oos && oos.sampleId ? !!limsReruns.selectRerunForOriginal(state, oos.sampleId) : false
  )
  // The sample's OPEN investigations — drives the unflag mirror after an atomic decide-close
  // (the sample stays flagged while another open investigation remains).
  const openInvestigationsForSample = useSelector(
    (state) => (oos && oos.sampleId ? limsOos.selectOpenInvestigationsForSample(state, oos.sampleId) : []),
    shallowEqual
  )

  // Local edit buffers for Phase 1 / Phase 2 — committed to the store on the explicit Save actions.
  const phase1 = oos && oos.phase1
  const phase2 = oos && oos.phase2
  const capas = (oos && oos.capas) || []

  // Phase-1 checklist edit buffer (keyed by check key); falls back to the persisted state.
  const [checks, setChecks] = useState(() => {
    const map = {}
    PHASE1_CHECK_KEYS.forEach((key) => {
      const found = (phase1 && phase1.checklist && phase1.checklist.find((c) => c.key === key)) || {}
      map[key] = { checked: !!found.checked, note: found.note || "" }
    })
    return map
  })
  const [assignableCause, setAssignableCause] = useState(!!(phase1 && phase1.assignableCauseFound))
  const [conclusion, setConclusion] = useState((phase1 && phase1.conclusion) || "")

  // Phase-2 edit buffers.
  const [resampling, setResampling] = useState((phase2 && phase2.resampling) || "")
  const [productionReview, setProductionReview] = useState((phase2 && phase2.productionReview) || "")
  const [stability, setStability] = useState((phase2 && phase2.stabilityAssessment) || "")
  const [outcome, setOutcome] = useState((phase2 && phase2.outcome) || "")

  // New-CAPA buffer.
  const [capaType, setCapaType] = useState(CAPA_TYPE.CORRECTIVE)
  const [capaDescription, setCapaDescription] = useState("")
  const [capaOwner, setCapaOwner] = useState("")
  const [capaDueDate, setCapaDueDate] = useState("")

  // Which e-signature dialog is open: "technician" | "supervisor" | "phase2" | "close" | null.
  const [signing, setSigning] = useState(null)
  // The close disposition QA selected: "" | "rerun" | "batch-hold".
  const [closeDisposition, setCloseDisposition] = useState("")
  const [saving, setSaving] = useState(false)

  const memberInitials = (id) => (id && members[id] && members[id].initials) || "—"

  // Member options for the CAPA owner (labelled by initials, like the rest of the LIMS UI).
  const memberOptions = useMemo(
    () =>
      Object.values(members || {})
        .filter((m) => m && (m.id || m.memberId))
        .map((m) => ({ id: m.id || m.memberId, name: m.initials || m.name || (m.id || m.memberId) })),
    [members]
  )

  if (!oos) return null

  const isClosed = oos.state === "closed"
  const readOnly = isClosed
  const inPhase2 = !!phase2
  // RR5 #7: Phase 1 (lab investigation) is editable ONLY while the investigation is still in the
  // lab-investigation stage (open / investigating). Once it has moved past that — escalated to QA
  // review, Phase 2 started, or closed — Phase 1 is a concluded, signed record and must be
  // read-only: otherwise the UI offers a "Save Phase 1" the backend rejects and a dead "Escalate
  // to Phase 2". The QA-review / close actions live in the footer and stay active (they are gated
  // on `readOnly`, not this flag).
  const phase1ReadOnly = readOnly || oos.state === "qa-review" || inPhase2

  // K7 — toggling the assignable-cause checkbox SUGGESTS the matching standard conclusion text, but
  // only into an empty field or one still holding the OPPOSITE standard text verbatim; user-authored
  // text is never overwritten (SPEC-022 / PRD-006 K7; data integrity). The suggest/contradict logic
  // is in pure helpers below (conclusionSuggestion / conclusionContradictsCause). The checkbox is
  // disabled when readOnly, so the toggle never fires on a closed investigation.
  const handleToggleCause = (nextCause) => {
    setAssignableCause(nextCause)
    setConclusion(conclusionSuggestion(t, conclusion, nextCause))
  }
  // The conclusion contradicts the checkbox when it still reads as the OPPOSITE standard text
  // (only meaningful while editable — a closed investigation is read-only).
  const conclusionContradicts = conclusionContradictsCause(t, conclusion, assignableCause, readOnly)

  // The current Phase-1 buffer, assembled for a save / signature.
  const buildPhase1 = (extra = {}) => ({
    checklist: PHASE1_CHECK_KEYS.map((key) => ({
      key,
      checked: !!checks[key].checked,
      note: checks[key].note || "",
    })),
    assignableCauseFound: assignableCause,
    conclusion,
    technicianSignature: (phase1 && phase1.technicianSignature) || null,
    supervisorSignature: (phase1 && phase1.supervisorSignature) || null,
    ...extra,
  })

  const setCheck = (key, patch) =>
    setChecks((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }))

  // Persist the Phase-1 buffer (without a new signature) — the server advances
  // open -> investigating itself (the patch never sends `state` — SPEC-021 M1).
  const handleSavePhase1 = () => {
    setSaving(true)
    dispatch(limsOos.savePhase1(oosId, buildPhase1()))
      .then(() => dispatch(message.success(t("limsOosPhase1Saved"))))
      .catch(() => dispatch(message.error(t("limsOosPhase1Failed"))))
      .finally(() => setSaving(false))
  }

  // Sign Phase 1 (technician or supervisor) — saves the buffer with the new signature attached;
  // the signature's password rides top-level and is verified server-side (analyst / reviewer
  // capability + 4-eyes). Returns the promise so the e-signature dialog shows a 403 inline.
  const handleSignPhase1 = (role, signature) => {
    setSaving(true)
    const extra =
      role === "technician"
        ? { technicianSignature: signature }
        : { supervisorSignature: signature }
    return dispatch(limsOos.savePhase1(oosId, buildPhase1(extra)))
      .then(() => {
        setSigning(null)
        dispatch(message.success(t("limsOosPhase1Saved")))
      })
      .finally(() => setSaving(false))
  }

  const handleEscalatePhase2 = () => {
    setSaving(true)
    dispatch(limsOos.escalateToPhase2(oosId))
      .then(() => dispatch(message.success(t("limsOosPhase2Escalated"))))
      .catch(() => dispatch(message.error(t("limsOosPhase2EscalateFailed"))))
      .finally(() => setSaving(false))
  }

  // RR2 #17: escalate the investigation to QA review (investigating -> qa-review) under a reviewer
  // e-signature (4-eyes != raiser, enforced server-side). On success the modal closes and the
  // Investigations list shows the "awaiting QA decision" marker. Returns the promise so the
  // e-signature dialog surfaces a backend 403 inline.
  const handleEscalateQaReview = (signature) => {
    setSaving(true)
    return dispatch(limsOos.escalateToQaReview(oosId, signature))
      .then(() => {
        setSigning(null)
        dispatch(message.success(t("limsOosSendToQaReview")))
        onClose()
      })
      .finally(() => setSaving(false))
  }

  // Sign + save Phase 2 (resampling / production review / stability / outcome + the signature).
  // Returns the promise so the e-signature dialog shows a backend 403 inline.
  const handleSignPhase2 = (signature) => {
    setSaving(true)
    return dispatch(
      limsOos.savePhase2(oosId, {
        resampling,
        productionReview,
        stabilityAssessment: stability,
        outcome: outcome || null,
        signature,
      })
    )
      .then(() => {
        setSigning(null)
        dispatch(message.success(t("limsOosPhase2Saved")))
      })
      .finally(() => setSaving(false))
  }

  const handleAddCapa = () => {
    if (!capaDescription.trim()) return
    setSaving(true)
    dispatch(
      limsOos.addCapa(oosId, {
        type: capaType,
        description: capaDescription.trim(),
        owner: capaOwner || "",
        dueDate: capaDueDate || null,
        status: CAPA_STATUS.OPEN,
      })
    )
      .then(() => {
        setCapaDescription("")
        setCapaOwner("")
        setCapaDueDate("")
        setCapaType(CAPA_TYPE.CORRECTIVE)
      })
      .catch(() => dispatch(message.error(t("limsCapaFailed"))))
      .finally(() => setSaving(false))
  }

  const handleCapaStatus = (capaId, status) => {
    dispatch(limsOos.updateCapa(oosId, capaId, { status })).catch(() =>
      dispatch(message.error(t("limsCapaFailed")))
    )
  }

  // SPEC-020 (C4) close-completeness gate, evaluated from the PERSISTED record (the close call
  // patches only the state, so unsaved local buffers must not count). The SERVER enforces the
  // same gate authoritatively (422 with precise messages, surfaced verbatim in the signature
  // dialog); this list at the Close button is the fail-fast mirror.
  const closeMissing = computeCloseMissing(readOnly, phase1, phase2)
  const canClose = closeMissing.length === 0

  // SPEC-021 (B7): close WITH a batch-impacting disposition goes through the ATOMIC
  // POST /lims/qaDecisions — ONE QA signature closes the OOS, records the decision and (for
  // batch-rejected) holds the lot in a single server transaction. The FE never follows up with
  // its own close/hold for these flows. "may-reanalyse": the FE then creates the rerun sample +
  // the /lims/reruns link (B-D5 — reruns are FE-created; the backend dedups). A close WITHOUT a
  // disposition uses the plain OOS close endpoint. Resolves to the i18n key of a warning to
  // surface (or undefined).
  const performClose = (signature) => {
    if (closeDisposition === DISPOSITION.RERUN) {
      return dispatch(
        limsQaDecisions.decide({
          oos,
          decision: QA_DECISION_TYPES.MAY_REANALYSE,
          comment: (signature && signature.reason) || "",
          batchLot: (sampleQc && sampleQc.batchLot) || null,
          signature,
        })
      ).then((decision) => {
        afterAtomicClose()
        if (hasExistingRerun || !oos.sampleId) {
          return "limsOosDispositionRerunSkipped"
        }
        return dispatch(
          limsReruns.createRerun({
            originalSample: resolveOriginalSample(oos),
            analysisId: oos.analysisId || null,
            hasApproval: true, // the signed QA decision IS the approval
            qaDecisionId: (decision && decision.id) || null,
          })
        )
          .then(() => undefined)
          .catch(() => "limsOosDispositionFailed")
      })
    }
    if (closeDisposition === DISPOSITION.BATCH_HOLD) {
      const lot = (sampleQc && sampleQc.batchLot) || null
      return dispatch(
        limsQaDecisions.decide({
          oos,
          decision: QA_DECISION_TYPES.BATCH_REJECTED,
          comment: (signature && signature.reason) || "",
          batchLot: lot,
          signature,
        })
      ).then(() => {
        afterAtomicClose()
        dispatch(limsBatchHold.fetch())
        // Without a registered lot the server records the decision + closes the OOS but can
        // hold nothing — surfaced, never silent (SPEC-020).
        return lot ? undefined : "limsOosDispositionHoldSkipped"
      })
    }
    return dispatch(limsOos.close(oosId, { signature, disposition: null })).then(() => undefined)
  }

  // After an ATOMIC decide the server has already closed the OOS (and unflagged the sample when
  // it was the last open one): re-read the OOS register and mirror the unflag in the FE samples
  // state (idempotent against the server's own unflag), exactly like the plain-close thunk does.
  const afterAtomicClose = () => {
    dispatch(limsOos.fetch())
    if (oos.sampleId) {
      const otherOpen = (openInvestigationsForSample || []).filter((o) => o.id !== oosId)
      if (otherOpen.length === 0) {
        dispatch(samples.unflagSample(oos.sampleId, oos.sampleLabel || ""))
      }
    }
  }

  // Close the investigation under the QA signature: validate close-eligibility FIRST (SPEC-020 —
  // defense in depth; the server gate is authoritative and its 422 messages surface verbatim in
  // the signature dialog), run the (atomic) close + optional disposition, surface any disposition
  // no-op as a warning. Returns the promise so the dialog stays open on a 403/422.
  const handleClose = (signature) => {
    if (!canClose) {
      dispatch(message.error(t("limsOosCloseMissingTitle")))
      return undefined
    }
    setSaving(true)
    return performClose(signature)
      .then((warnKey) => {
        if (warnKey) dispatch(message.warning(t(warnKey)))
        setSigning(null)
        dispatch(message.success(t("limsOosCloseDone")))
        if (onClosed) onClosed()
        onClose()
      })
      .finally(() => setSaving(false))
  }

  // Phase 2 is only valid when no assignable (lab) cause was found.
  const canEscalate = !readOnly && !inPhase2 && !assignableCause

  // SPEC-020 signature segregation — see signatureExclusions() below.
  const { supervisorExcluded, closeExcluded } = signatureExclusions(oos, phase1, phase2)

  const outcomeOptions = [
    { id: PHASE2_OUTCOME.LOT_APPROVED, name: t("limsOosOutcomeApproved") },
    { id: PHASE2_OUTCOME.LOT_REJECTED, name: t("limsOosOutcomeRejected") },
    { id: PHASE2_OUTCOME.RECALLED, name: t("limsOosOutcomeRecalled") },
  ]
  const capaTypeOptions = [
    { id: CAPA_TYPE.CORRECTIVE, name: t("limsCapaTypeCorrective") },
    { id: CAPA_TYPE.PREVENTIVE, name: t("limsCapaTypePreventive") },
  ]
  const dispositionOptions = [
    { id: DISPOSITION.RERUN, name: t("limsOosDispositionRerun") },
    { id: DISPOSITION.BATCH_HOLD, name: t("limsOosDispositionBatchHold") },
  ]
  const capaStatusOptions = [
    { id: CAPA_STATUS.OPEN, name: t("limsCapaStatusOpen") },
    { id: CAPA_STATUS.IN_PROGRESS, name: t("limsCapaStatusInProgress") },
    { id: CAPA_STATUS.CLOSED, name: t("limsCapaStatusClosed") },
  ]

  const signatureSummary = (sig) => {
    if (!sig) return t("none")
    const initials = memberInitials(sig.memberId)
    return sig.reason ? `${initials} — ${sig.reason}` : initials
  }

  return (
    <>
      <Popup title={t("limsOosInvestigationTitle")} onCancel={onClose}>
        <div className="OosInvestigationModal">
          {/* Context */}
          <div className="OosInvestigationModal-context">
            <div className="OosInvestigationModal-context-item">
              <span className={LABEL_CLASS}>{t("limsOosSample")}</span>
              <span>{oos.sampleLabel || "—"}</span>
            </div>
            <div className="OosInvestigationModal-context-item">
              <span className={LABEL_CLASS}>{t("limsOosAnalysis")}</span>
              <span>{oos.analysisLabel || "—"}</span>
            </div>
            <div className="OosInvestigationModal-context-item">
              <span className={LABEL_CLASS}>{t("limsOosReason")}</span>
              <span>{oos.reason || "—"}</span>
            </div>
            <div className="OosInvestigationModal-context-item">
              <span className={LABEL_CLASS}>{t("limsOosState")}</span>
              <span className={`OosInvestigationModal-state is-${oos.state}`}>
                {t(stateLabelKey(oos.state))}
              </span>
              {/* K13 (c): a reopened investigation carries a reopenHistory — show a compact note (the
                  count + who last reopened it and when) next to the state chip so the correction is
                  visible. The original close signature + audit are preserved server-side. */}
              <ReopenNote history={oos.reopenHistory} memberInitials={memberInitials} t={t} />
            </div>
          </div>

          {/* Phase 1 — lab investigation */}
          <Phase1Section
            t={t}
            phase1={phase1}
            checks={checks}
            setCheck={setCheck}
            assignableCause={assignableCause}
            onToggleCause={handleToggleCause}
            conclusion={conclusion}
            setConclusion={setConclusion}
            conclusionContradicts={conclusionContradicts}
            readOnly={phase1ReadOnly}
            saving={saving}
            canEscalate={canEscalate}
            signatureSummary={signatureSummary}
            onSign={setSigning}
            onSavePhase1={handleSavePhase1}
            onEscalatePhase2={handleEscalatePhase2}
          />

          {/* Phase 2 — product investigation (only once escalated) */}
          {inPhase2 && (
            <section className="OosInvestigationModal-section">
              <h4 className="OosInvestigationModal-section-title">{t("limsOosPhase2Title")}</h4>

              <label className="OosInvestigationModal-field-label">{t("limsOosResampling")}</label>
              <Form.Control
                as="textarea"
                rows={2}
                value={resampling}
                disabled={readOnly}
                onChange={(e) => setResampling(e.target.value)}
              />

              <label className="OosInvestigationModal-field-label">{t("limsOosProductionReview")}</label>
              <Form.Control
                as="textarea"
                rows={2}
                value={productionReview}
                disabled={readOnly}
                onChange={(e) => setProductionReview(e.target.value)}
              />

              <label className="OosInvestigationModal-field-label">{t("limsOosStability")}</label>
              <Form.Control
                as="textarea"
                rows={2}
                value={stability}
                disabled={readOnly}
                onChange={(e) => setStability(e.target.value)}
              />

              <label className="OosInvestigationModal-field-label">{t("limsOosOutcome")}</label>
              <Select
                isMulti={false}
                includeNone
                noneLabel={t("limsOosOutcomeChoose")}
                disabled={readOnly}
                collection={outcomeOptions}
                value={outcome || ""}
                onChange={(v) => setOutcome(firstOf(v) || "")}
                menuPlacement="auto"
              />

              <div className="OosInvestigationModal-sig">
                <span className={LABEL_CLASS}>{t("limsOosPhase2Signature")}</span>
                <span>{signatureSummary(phase2 && phase2.signature)}</span>
              </div>

              {!readOnly && (
                <div className="OosInvestigationModal-section-actions">
                  <Button size="sm" variant="primary" onClick={() => setSigning("phase2")} disabled={saving}>
                    {t("limsOosSavePhase2")}
                  </Button>
                </div>
              )}
            </section>
          )}

          {/* CAPA */}
          <CapaSection
            t={t}
            capas={capas}
            readOnly={readOnly}
            saving={saving}
            memberInitials={memberInitials}
            capaTypeOptions={capaTypeOptions}
            capaStatusOptions={capaStatusOptions}
            memberOptions={memberOptions}
            capaType={capaType}
            setCapaType={setCapaType}
            capaDescription={capaDescription}
            setCapaDescription={setCapaDescription}
            capaOwner={capaOwner}
            setCapaOwner={setCapaOwner}
            capaDueDate={capaDueDate}
            setCapaDueDate={setCapaDueDate}
            onAddCapa={handleAddCapa}
            onCapaStatus={handleCapaStatus}
          />

          {/* Close — QA signature + optional disposition */}
          {!readOnly && (
            <CloseSection
              t={t}
              dispositionOptions={dispositionOptions}
              closeDisposition={closeDisposition}
              setCloseDisposition={setCloseDisposition}
              hasExistingRerun={hasExistingRerun}
              hasLot={!!(sampleQc && sampleQc.batchLot)}
              closeMissing={closeMissing}
            />
          )}

          <div className="OosInvestigationModal-actions">
            <Button variant="light" onClick={onClose} disabled={saving}>
              {readOnly ? t("close") : t("cancel")}
            </Button>
            {/* RR2 #17: reviewer hands a still-investigating OOS to QA (e-signed escalation). */}
            {!readOnly && oos.state === "investigating" && (
              <Button variant="primary" onClick={() => setSigning("escalate-qa")} disabled={saving}>
                {t("limsOosSendToQaReview")}
              </Button>
            )}
            {!readOnly && (
              <Button
                variant="primary"
                onClick={() => setSigning("close")}
                disabled={saving || !canClose}
                title={!canClose ? t("limsOosCloseMissingTitle") : undefined}
              >
                {t("limsOosCloseInvestigation")}
              </Button>
            )}
          </div>
        </div>
      </Popup>

      <InvestigationSignatures
        signing={signing}
        t={t}
        supervisorExcluded={supervisorExcluded}
        closeExcluded={closeExcluded}
        escalateExcluded={[oos.raisedBy].filter(Boolean)}
        onCancel={() => setSigning(null)}
        onSignPhase1={handleSignPhase1}
        onSignPhase2={handleSignPhase2}
        onEscalateQaReview={handleEscalateQaReview}
        onClose={handleClose}
      />
    </>
  )
}

// The Phase-1 (laboratory investigation) section: the six-point checklist, the assignable-cause
// finding, the conclusion, the technician/supervisor signature captions, and the Save / Escalate
// actions. Pulled out so the main component's render stays within complexity limits.
function Phase1Section({
  t,
  phase1,
  checks,
  setCheck,
  assignableCause,
  onToggleCause,
  conclusion,
  setConclusion,
  conclusionContradicts,
  readOnly,
  saving,
  canEscalate,
  signatureSummary,
  onSign,
  onSavePhase1,
  onEscalatePhase2,
}) {
  return (
    <section className="OosInvestigationModal-section">
      <h4 className="OosInvestigationModal-section-title">{t("limsOosPhase1Title")}</h4>
      <Table responsive className="OosInvestigationModal-checklist">
        <thead>
          <tr>
            <th></th>
            <th>{t("limsOosCheckPoint")}</th>
            <th>{t("limsOosCheckNote")}</th>
          </tr>
        </thead>
        <tbody>
          {PHASE1_CHECK_KEYS.map((key) => (
            <tr key={key}>
              <td className="OosInvestigationModal-check-box">
                <Form.Check
                  type="checkbox"
                  checked={!!checks[key].checked}
                  disabled={readOnly}
                  onChange={(e) => setCheck(key, { checked: e.target.checked })}
                  id={`oos-check-${key}`}
                />
              </td>
              <td>{t(checkLabelKey(key))}</td>
              <td>
                <Form.Control
                  type="text"
                  value={checks[key].note}
                  disabled={readOnly}
                  onChange={(e) => setCheck(key, { note: e.target.value })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Form.Check
        type="checkbox"
        className="OosInvestigationModal-cause"
        checked={assignableCause}
        disabled={readOnly}
        onChange={(e) => onToggleCause(e.target.checked)}
        id="oos-assignable-cause"
        label={t("limsOosAssignableCause")}
      />
      {!assignableCause && <p className="OosInvestigationModal-hint">{t("limsOosNoCause")}</p>}

      <label className="OosInvestigationModal-field-label">{t("limsOosConclusion")}</label>
      <Form.Control
        as="textarea"
        rows={2}
        value={conclusion}
        disabled={readOnly}
        onChange={(e) => setConclusion(e.target.value)}
      />
      {conclusionContradicts && (
        <p className="OosInvestigationModal-warning">{t("limsOosConclusionContradicts")}</p>
      )}

      <div className="OosInvestigationModal-signatures">
        <div className="OosInvestigationModal-sig">
          <span className={LABEL_CLASS}>{t("limsOosTechnicianSignature")}</span>
          <span>{signatureSummary(phase1 && phase1.technicianSignature)}</span>
          {!readOnly && (
            <Button size="sm" variant="outline-secondary" onClick={() => onSign("technician")}>
              {t("limsOosSignTechnician")}
            </Button>
          )}
        </div>
        <div className="OosInvestigationModal-sig">
          <span className={LABEL_CLASS}>{t("limsOosSupervisorSignature")}</span>
          <span>{signatureSummary(phase1 && phase1.supervisorSignature)}</span>
          {!readOnly && (
            <Button size="sm" variant="outline-secondary" onClick={() => onSign("supervisor")}>
              {t("limsOosSignSupervisor")}
            </Button>
          )}
        </div>
      </div>

      {!readOnly && (
        <div className="OosInvestigationModal-section-actions">
          <Button size="sm" variant="primary" onClick={onSavePhase1} disabled={saving}>
            {t("limsOosSavePhase1")}
          </Button>
          <Button
            size="sm"
            variant="warning"
            onClick={onEscalatePhase2}
            disabled={saving || !canEscalate}
            title={!canEscalate ? t("limsOosEscalatePhase2Hint") : undefined}
          >
            {t("limsOosEscalatePhase2")}
          </Button>
        </div>
      )}
    </section>
  )
}

// The embedded CAPA list + add-row, pulled out of the main component so its render stays within
// complexity limits. Read-only when the investigation is closed (the existing CAPAs are still shown,
// but no status edits / new CAPAs).
function CapaSection({
  t,
  capas,
  readOnly,
  saving,
  memberInitials,
  capaTypeOptions,
  capaStatusOptions,
  memberOptions,
  capaType,
  setCapaType,
  capaDescription,
  setCapaDescription,
  capaOwner,
  setCapaOwner,
  capaDueDate,
  setCapaDueDate,
  onAddCapa,
  onCapaStatus,
}) {
  return (
    <section className="OosInvestigationModal-section">
      <h4 className="OosInvestigationModal-section-title">{t("limsCapaTitle")}</h4>
      {capas.length === 0 ? (
        <p className="OosInvestigationModal-hint">{t("limsCapaNone")}</p>
      ) : (
        <Table responsive className="OosInvestigationModal-capas">
          <thead>
            <tr>
              <th>{t("limsCapaTypeColumn")}</th>
              <th>{t("limsCapaDescription")}</th>
              <th>{t("limsCapaOwner")}</th>
              <th>{t("limsCapaDueDate")}</th>
              <th>{t("limsCapaStatus")}</th>
            </tr>
          </thead>
          <tbody>
            {capas.map((c) => (
              <tr key={c.id}>
                <td>{t(capaTypeLabelKey(c.type))}</td>
                <td>{c.description || "—"}</td>
                <td>{c.owner ? memberInitials(c.owner) : "—"}</td>
                <td>{c.dueDate || "—"}</td>
                <td className="OosInvestigationModal-capa-status">
                  {readOnly ? (
                    <span>{t(capaStatusLabelKey(c.status))}</span>
                  ) : (
                    <Select
                      isMulti={false}
                      collection={capaStatusOptions}
                      value={c.status || CAPA_STATUS.OPEN}
                      onChange={(v) => onCapaStatus(c.id, firstOf(v) || CAPA_STATUS.OPEN)}
                      menuPlacement="auto"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {!readOnly && (
        <div className="OosInvestigationModal-capa-add">
          <Select
            isMulti={false}
            collection={capaTypeOptions}
            value={capaType}
            onChange={(v) => setCapaType(firstOf(v) || CAPA_TYPE.CORRECTIVE)}
            menuPlacement="auto"
          />
          <Form.Control
            type="text"
            placeholder={t("limsCapaDescriptionPlaceholder")}
            value={capaDescription}
            onChange={(e) => setCapaDescription(e.target.value)}
          />
          <Select
            isMulti={false}
            includeNone
            noneLabel={t("limsCapaOwnerChoose")}
            collection={memberOptions}
            value={capaOwner || ""}
            onChange={(v) => setCapaOwner(firstOf(v) || "")}
            menuPlacement="auto"
          />
          <Form.Control type="date" value={capaDueDate} onChange={(e) => setCapaDueDate(e.target.value)} />
          <Button
            size="sm"
            variant="outline-primary"
            onClick={onAddCapa}
            disabled={saving || !capaDescription.trim()}
          >
            {t("limsCapaAdd")}
          </Button>
        </div>
      )}
    </section>
  )
}

// The "Close investigation" section: the optional disposition choice (rerun / batch-hold), the
// hints for a no-op disposition, and the SPEC-020 close-gate list of everything still missing
// before the investigation may be closed. Pulled out of the main component so its render stays
// within complexity limits.
function CloseSection({
  t,
  dispositionOptions,
  closeDisposition,
  setCloseDisposition,
  hasExistingRerun,
  hasLot,
  closeMissing,
}) {
  return (
    <section className="OosInvestigationModal-section OosInvestigationModal-close">
      <h4 className="OosInvestigationModal-section-title">{t("limsOosCloseInvestigation")}</h4>
      <label className="OosInvestigationModal-field-label">{t("limsOosDisposition")}</label>
      <Select
        isMulti={false}
        includeNone
        noneLabel={t("limsOosDispositionNone")}
        collection={dispositionOptions}
        value={closeDisposition || ""}
        onChange={(v) => setCloseDisposition(firstOf(v) || "")}
        menuPlacement="auto"
      />
      {closeDisposition === DISPOSITION.RERUN && hasExistingRerun && (
        <p className="OosInvestigationModal-hint">{t("limsOosDispositionRerunExists")}</p>
      )}
      {closeDisposition === DISPOSITION.BATCH_HOLD && !hasLot && (
        <p className="OosInvestigationModal-hint">{t("limsOosDispositionNoLot")}</p>
      )}
      {closeMissing.length > 0 && (
        <div className="OosInvestigationModal-close-missing">
          <span className={LABEL_CLASS}>{t("limsOosCloseMissingTitle")}</span>
          <ul>
            {closeMissing.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

// The four e-signature dialogs (technician / supervisor Phase-1, Phase-2, QA close), pulled out of
// the main component so its render stays within complexity limits. Exactly one is shown at a time,
// keyed by `signing`. Signature segregation (SPEC-020): the supervisor dialog excludes the Phase-1
// technician signer; the QA-close dialog excludes the raiser + all investigation signers.
function InvestigationSignatures({
  signing,
  t,
  supervisorExcluded,
  closeExcluded,
  escalateExcluded,
  onCancel,
  onSignPhase1,
  onSignPhase2,
  onEscalateQaReview,
  onClose,
}) {
  if (signing === "technician") {
    return (
      <ESignatureModal
        title={t("limsOosSignTechnician")}
        reasons={["reviewed"]}
        onSign={(sig) => onSignPhase1("technician", sig)}
        onCancel={onCancel}
      />
    )
  }
  if (signing === "supervisor") {
    return (
      <ESignatureModal
        title={t("limsOosSignSupervisor")}
        reasons={["reviewed", "approved"]}
        requiredCapability="reviewer"
        excludedMemberIds={supervisorExcluded}
        onSign={(sig) => onSignPhase1("supervisor", sig)}
        onCancel={onCancel}
      />
    )
  }
  if (signing === "phase2") {
    return (
      <ESignatureModal
        title={t("limsOosSavePhase2")}
        reasons={["reviewed", "approved"]}
        requiredCapability="reviewer"
        onSign={onSignPhase2}
        onCancel={onCancel}
      />
    )
  }
  if (signing === "close") {
    return (
      <ESignatureModal
        title={t("limsOosCloseInvestigation")}
        reasons={["approved"]}
        requiredCapability="qa"
        excludedMemberIds={closeExcluded}
        onSign={onClose}
        onCancel={onCancel}
      />
    )
  }
  if (signing === "escalate-qa") {
    return (
      <ESignatureModal
        title={t("limsOosSendToQaReview")}
        reasons={["approved"]}
        requiredCapability="reviewer"
        excludedMemberIds={escalateExcluded}
        onSign={onEscalateQaReview}
        onCancel={onCancel}
      />
    )
  }
  return null
}

// The single chosen value from a Select onChange payload (array for multi, scalar otherwise).
function firstOf(v) {
  return Array.isArray(v) ? v[0] : v
}

// K13 (c): the reopen-history note shown next to the state chip. `history` is the record's
// `reopenHistory` array (each entry the prior close + this reopen — the FE reads it defensively for
// the reopener + timestamp). Renders the count + who last reopened it and when; nothing when never
// reopened. Field names are read with fallbacks so a small server-shape change does not blank it.
function ReopenNote({ history, memberInitials, t }) {
  if (!Array.isArray(history) || history.length === 0) return null
  const last = history[history.length - 1]
  const by = memberInitials(last.reopenedBy || last.by || last.memberId)
  const atIso = last.reopenedAt || last.at || last.timestamp
  const at = atIso ? DateTime.fromISO(atIso).toFormat("dd/MM/yyyy HH:mm") : "—"
  return (
    <span className="OosInvestigationModal-reopen-note" title={last.reason || last.reopenReason || ""}>
      {t("limsReopenHistoryNote", { count: history.length, by: by || "—", at })}
    </span>
  )
}

// Resolve the original sample (id + number + product) for a rerun disposition. The sampleLabel
// is "name (observationId)"; take the part before the trailing parenthesis without a regex
// (avoids a backtracking-prone pattern on untrusted-length input).
function resolveOriginalSample(oos) {
  const label = oos.sampleLabel || ""
  const parenAt = label.lastIndexOf(" (")
  const sampleName = parenAt > 0 ? label.slice(0, parenAt) : label
  return {
    id: oos.sampleId,
    sampleName,
    productName: null,
    analysisIds: oos.analysisId ? [oos.analysisId] : [],
  }
}

// K7 — the standard conclusion text that matches an assignable-cause answer (i18n).
function conclusionFor(t, cause) {
  return cause ? t("limsOosConclusionAssignable") : t("limsOosConclusionNoCause")
}

// K7 — the conclusion text to set when the checkbox toggles to `nextCause`. Only an empty field or
// one still holding the OPPOSITE standard text verbatim is replaced with the matching standard text;
// any other (user-authored) content is returned unchanged so it is never overwritten.
function conclusionSuggestion(t, conclusion, nextCause) {
  const trimmed = (conclusion || "").trim()
  const replaceable = trimmed === "" || trimmed === conclusionFor(t, !nextCause).trim()
  return replaceable ? conclusionFor(t, nextCause) : conclusion
}

// K7 — true when the conclusion field still reads as the OPPOSITE standard text for the current
// checkbox answer (the two contradict; a non-blocking warning hint is shown). Never flagged on a
// read-only (closed) investigation.
function conclusionContradictsCause(t, conclusion, cause, readOnly) {
  if (readOnly) return false
  return (conclusion || "").trim() === conclusionFor(t, !cause).trim()
}

// SPEC-020 (C4): the i18n keys of everything still missing before the investigation may be
// closed. Phase 1 must be complete — every checklist point done, a conclusion, and BOTH the
// technician and supervisor signatures — and, when escalated, Phase 2 must carry an outcome.
// A closed (read-only) investigation has nothing missing by definition.
function computeCloseMissing(readOnly, phase1, phase2) {
  if (readOnly) return []
  const missing = []
  const checklist = (phase1 && phase1.checklist) || []
  const allChecked = checklist.length > 0 && checklist.every((c) => c.checked)
  if (!allChecked) missing.push("limsOosCloseMissingChecklist")
  if (!(phase1 && phase1.conclusion && phase1.conclusion.trim())) missing.push("limsOosCloseMissingConclusion")
  if (!(phase1 && phase1.technicianSignature)) missing.push("limsOosCloseMissingTechSig")
  if (!(phase1 && phase1.supervisorSignature)) missing.push("limsOosCloseMissingSupSig")
  if (phase2 && !phase2.outcome) missing.push("limsOosCloseMissingPhase2Outcome")
  return missing
}

// SPEC-020 signature segregation: the supervisor counter-signature may not come from the Phase-1
// technician signer, and the QA close may not come from the raiser NOR any of the technician /
// supervisor / Phase-2 signers — QA must be a fresh set of eyes on the whole investigation.
function signatureExclusions(oos, phase1, phase2) {
  const technicianSignerId =
    (phase1 && phase1.technicianSignature && phase1.technicianSignature.memberId) || null
  const supervisorExcluded = technicianSignerId ? [technicianSignerId] : []
  const closeExcluded = Array.from(
    new Set(
      [
        oos.raisedBy,
        technicianSignerId,
        phase1 && phase1.supervisorSignature && phase1.supervisorSignature.memberId,
        phase2 && phase2.signature && phase2.signature.memberId,
      ].filter(Boolean)
    )
  )
  return { supervisorExcluded, closeExcluded }
}

// i18n key for a Phase-1 checklist point.
function checkLabelKey(key) {
  switch (key) {
    case "calculations":
      return "limsOosCheckCalculations"
    case "weighings":
      return "limsOosCheckWeighings"
    case "standard":
      return "limsOosCheckStandard"
    case "system":
      return "limsOosCheckSystem"
    case "reinjection":
      return "limsOosCheckReinjection"
    default:
      return "limsOosCheckConclusion"
  }
}

// i18n key for an OOS lifecycle state.
function stateLabelKey(state) {
  switch (state) {
    case "investigating":
      return "limsOosStateInvestigating"
    case "qa-review":
      return "limsOosStateQaReview"
    case "closed":
      return "limsOosStateClosed"
    default:
      return "limsOosStateOpen"
  }
}

// i18n key for a CAPA type.
function capaTypeLabelKey(type) {
  return type === CAPA_TYPE.PREVENTIVE ? "limsCapaTypePreventive" : "limsCapaTypeCorrective"
}

// i18n key for a CAPA status.
function capaStatusLabelKey(status) {
  switch (status) {
    case CAPA_STATUS.IN_PROGRESS:
      return "limsCapaStatusInProgress"
    case CAPA_STATUS.CLOSED:
      return "limsCapaStatusClosed"
    default:
      return "limsCapaStatusOpen"
  }
}
